import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip as ChartTooltip, CartesianGrid, Legend,
} from 'recharts';
import { gridFromResult, profileAlongLine, paddedDomain } from '../lib/grid-ops';
import { resistivityColor, sectionCells } from '../lib/inversion-parser';
import { computeDoiProfile, doiLine, makeDoiMask, isDivergingProp, divergingColor, divergingLimit } from '../lib/inversion-results';
import TimelapseDateSlider from './TimelapseDateSlider';
import useDoiSettings from '../hooks/useDoiSettings';
import { useTheme } from '../contexts/ThemeContext';
import useSectionTerrain from '../hooks/useSectionTerrain';
import SectionTerrainOverlay from './terrain/SectionTerrainOverlay';
import useTerrainSettings from '../hooks/useTerrainSettings';
import isolines from '@turf/isolines';
import { loadAppearance, saveAppearance, clearAppearance, defaultAppearance, textStyle, inkFor } from '../lib/section-appearance';
import SectionAppearanceControls from './section/SectionAppearanceControls';
import SectionLegendBottom from './section/SectionLegendBottom';
import { makeLevels, classify, rampColor, legendTicks, fmtLevel, SECTION_COLORMAPS } from '../lib/section-levels';

const OPTS_KEY = 'section.opts';
const PERSIST_KEYS = ['colormap', 'reverse', 'logScale', 'classes', 'interval', 'count', 'contours',
  'contoursSame', 'contourInterval', 'contourCount', 'contourColor', 'contourLabels'];
const DEFAULT_OPTS = {
  ve: 2, logScale: true, cmin: '', cmax: '',
  showDoi: true, sensOpacity: false, showElectrodes: true,
  colormap: 'earthimager', reverse: false, classes: 'levels', interval: '', count: 10,
  contours: false, contoursSame: true, contourInterval: '', contourCount: 10, contourColor: 'black', contourLabels: true,
};
function loadOpts() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(OPTS_KEY) || '{}') || {};
    const out = { ...DEFAULT_OPTS };
    PERSIST_KEYS.forEach(k => { if (saved[k] !== undefined) out[k] = saved[k]; });
    return out;
  } catch { return { ...DEFAULT_OPTS }; }
}
const CONTOUR_COLORS = { black: '#000000', white: '#ffffff', grey: '#808080' };
const MAX_GRID_W = 400, MAX_GRID_H = 200;

/** Sample the cells onto a regular (x, y) grid (y = depth shifted by topography); NaN outside the model. */
function sampleGrid(cells, property, logOn, off) {
  const rects = [];
  const widths = [], heights = [];
  cells.forEach((c) => {
    const v = c[property];
    if (typeof v !== 'number' || (logOn && !(v > 0))) return;
    const xa = c.x0 !== undefined ? c.x0 : c.x - 0.5, xb = c.x1 !== undefined ? c.x1 : c.x + 0.5;
    const ya = off(c) + (c.top !== undefined ? c.top : c.depth), yb = off(c) + (c.bottom !== undefined ? c.bottom : c.depth + 1);
    rects.push([xa, xb, ya, yb, logOn ? Math.log10(v) : v]);
    widths.push(xb - xa); heights.push(yb - ya);
  });
  if (!rects.length) return null;
  const med = (a) => a.slice().sort((p, q) => p - q)[Math.floor(a.length / 2)] || 1;
  let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
  rects.forEach(r => { xmin = Math.min(xmin, r[0]); xmax = Math.max(xmax, r[1]); ymin = Math.min(ymin, r[2]); ymax = Math.max(ymax, r[3]); });
  const dx = med(widths) / 2, dy = med(heights) / 2;
  const nx = Math.ceil((xmax - xmin) / dx) + 1, ny = Math.ceil((ymax - ymin) / dy) + 1;
  if (nx < 2 || ny < 2 || nx > MAX_GRID_W || ny > MAX_GRID_H) return null; // too big: skip contours
  const vals = new Float64Array(nx * ny).fill(NaN);
  rects.forEach(([xa, xb, ya, yb, v]) => {
    const i0 = Math.max(0, Math.ceil((xa - xmin) / dx - 1e-9)), i1 = Math.min(nx - 1, Math.ceil((xb - xmin) / dx - 1e-9) - 1);
    const j0 = Math.max(0, Math.ceil((ya - ymin) / dy - 1e-9)), j1 = Math.min(ny - 1, Math.ceil((yb - ymin) / dy - 1e-9) - 1);
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) vals[j * nx + i] = v;
  });
  return { vals, nx, ny, xmin, ymin, dx, dy };
}

/** Isolines of the sampled grid for `breaks` (value units), as [{ value, pts:[[x,y]...] }] in (x, y-depth) space. */
function contourLines(grid, breaks, logOn) {
  const { vals, nx, ny, xmin, ymin, dx, dy } = grid;
  const feats = [];
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    // y is stored negated so "north" is up, as the isolines grid expects; undone below
    feats.push({ type: 'Feature', properties: { value: vals[j * nx + i] }, geometry: { type: 'Point', coordinates: [xmin + i * dx, -(ymin + j * dy)] } });
  }
  const levels = breaks.map(b => (logOn ? Math.log10(b) : b));
  const fc = isolines({ type: 'FeatureCollection', features: feats }, levels, { zProperty: 'value' });
  const out = [];
  (fc.features || []).forEach((f) => {
    const raw = parseFloat(f.properties?.value ?? f.properties?.level);
    const value = logOn ? Math.pow(10, raw) : raw;
    const g = f.geometry;
    const parts = g.type === 'MultiLineString' ? g.coordinates : [g.coordinates];
    parts.forEach((line) => {
      let cur = [];
      const flush = () => { if (cur.length > 1) out.push({ value, pts: cur }); cur = []; };
      line.forEach(([x, y]) => { if (Number.isFinite(x) && Number.isFinite(y)) cur.push([x, -y]); else flush(); });
      flush();
    });
  });
  return out;
}

function ContourLayer({ cells, property, logOn, off, px, py, breaks, color, labels, clip, ts = {} }) {
  // Memoised by value (breaks and off are rebuilt on every render), so no hook deps are involved:
  // the CRA build's eslint has no react-hooks plugin and rejects a disable comment for it.
  const cacheRef = useRef({ cells: null, key: null, lines: [] });
  const key = `${property}|${logOn}|${breaks.join(',')}|${off.key}`;
  if (cacheRef.current.cells !== cells || cacheRef.current.key !== key) {
    const grid = sampleGrid(cells, property, logOn, off);
    cacheRef.current = { cells, key, lines: grid ? contourLines(grid, breaks, logOn) : [] };
  }
  const lines = cacheRef.current.lines;
  const stroke = CONTOUR_COLORS[color] || '#000';
  const halo = color === 'white' ? '#000' : '#fff';
  return (
    <g data-testid="section-contours-layer" clipPath={clip}>
      {lines.map((l, i) => {
        const pts = l.pts.map(([x, y]) => [px(x), py(y)]);
        const d = 'M ' + pts.map(p => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L ');
        const m = pts[Math.floor(pts.length / 2)];
        return (
          <g key={i}>
            <path data-testid="section-contour-lines" d={d} fill="none" stroke={stroke} strokeWidth={0.8} opacity={0.9} />
            {labels && pts.length > 3 && (
              <text x={m[0]} y={m[1]} fontSize={8} fontFamily={ts.fontFamily} fontWeight={ts.fontWeight} fontStyle={ts.fontStyle} textAnchor="middle" dominantBaseline="middle" fill={stroke}
                stroke={halo} strokeWidth={2.5} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{fmtLevel(l.value)}</text>
            )}
          </g>
        );
      })}
    </g>
  );
}

const PROFILE_COLORS = ['#00d4ff', '#fbbf24', '#34d399', '#f87171', '#a78bfa', '#fb923c'];

/** Filled inversion cross-section — colored blocks, log scale, DOI line + fade,
 *  vertical exaggeration, sensitivity opacity, electrode markers (EarthImager-style).
 *  With topography (cells carry `surface`, an elevation) the blocks are drawn against the
 *  ground-surface line: the vertical axis is elevation, every block keeps its depth. */
function FilledSection({ model, cells, property = 'resistivity', svgRef, options = {}, overlay = null, appearance = defaultAppearance('dark'), title = '' }) {
  const ap = appearance;
  const ts = textStyle(ap);
  const tsSmall = textStyle(ap, 0.85);
  const ink = inkFor(ap.plotBg);
  const bottomLegend = ap.legendPosition === 'bottom';
  const titleStr = ap.titleText || title;
  const showTitle = ap.showTitle && !!titleStr;
  const {
    ve = 1, logScale = property === 'resistivity',
    cmin = null, cmax = null,
    showDoi = true, sensOpacity = false, showElectrodes = true,
    colormap = 'earthimager', reverse = false, classes = 'levels', interval = '', count = 10,
    contours = false, contoursSame = true, contourInterval = '', contourCount = 10, contourColor = 'black', contourLabels = true,
  } = options;
  const doiSettings = useDoiSettings(); // global DOI mask + sensitivity threshold (InversionPanel)

  // Time-lapse differences (% change, absolute change, log ratio) use a blue-white-red ramp centred on zero
  const diverging = isDivergingProp(property);
  const logOn = logScale && !diverging;
  const valid = cells.filter(c => typeof c[property] === 'number' && (!logOn || c[property] > 0));
  // 1D soundings (geometryKind 'point') are drawn as a layered column across the plot
  const isPoint = model.geometryKind === 'point' || model.xRange[1] <= model.xRange[0];
  const [x0, x1] = isPoint ? [model.xRange[0] - 1, model.xRange[0] + 1] : model.xRange;
  // Topography: shift every cell down by (highest surface - its local surface)
  const topo = valid.some(c => typeof c.surface === 'number' && c.bottom !== undefined);
  const surfaceMax = topo ? valid.reduce((m, c) => (typeof c.surface === 'number' && c.surface > m ? c.surface : m), -Infinity) : 0;
  const off = (c) => (topo && typeof c.surface === 'number' ? surfaceMax - c.surface : 0);
  const contourOff = Object.assign((c) => off(c), { key: `${topo}:${surfaceMax}` });
  const maxDepth = (topo ? valid.reduce((m, c) => Math.max(m, off(c) + (c.bottom ?? c.depth)), 0) : model.depthRange[1]) * 1.05;

  // Vertical exaggeration: plot height reflects the true depth/width ratio × VE
  const W = 900, mL = 46, mB = bottomLegend ? 70 : 26, mT = showTitle ? 14 + Math.round(ap.fontSize * 1.6) : 10, mR = bottomLegend ? 20 : 60;
  const plotW = W - mL - mR;
  const plotH = Math.max(90, Math.min(620, (maxDepth / ((x1 - x0) || 1)) * plotW * ve));
  const H = plotH + mT + mB;

  if (valid.length === 0) return <div style={{ color: '#6e7681', fontSize: 12, padding: 20 }}>No {property} values in this model.</div>;

  const xs = [...new Set(valid.map(c => c.x))].sort((a, b) => a - b);
  const ds = [...new Set(valid.map(c => c.depth))].sort((a, b) => a - b);
  const dx = xs.length > 1 ? Math.min(...xs.slice(1).map((x, i) => x - xs[i])) : (x1 - x0) / 10;

  const useLog = logOn;
  const tf = useLog ? Math.log10 : (v) => v;
  const tVals = valid.map(c => tf(c[property]));
  let tMin = Math.min(...tVals.slice(0, 50000)), tMax = Math.max(...tVals.slice(0, 50000));
  if (cmin !== null && cmin !== '' && isFinite(parseFloat(cmin))) tMin = tf(parseFloat(cmin));
  if (cmax !== null && cmax !== '' && isFinite(parseFloat(cmax))) tMax = tf(parseFloat(cmax));
  if (!(tMax > tMin)) tMax = tMin + 1;
  // Diverging: symmetric limits so zero is white
  const divLim = diverging ? Math.max(Math.abs(cmin !== null && cmin !== '' && isFinite(parseFloat(cmin)) ? parseFloat(cmin) : 0),
    Math.abs(cmax !== null && cmax !== '' && isFinite(parseFloat(cmax)) ? parseFloat(cmax) : 0)) || divergingLimit(tVals.slice(0, 50000)) : 0;
  // Surfer-style levels: explicit class breaks (value units) drive both fill classes and the legend
  const levelsOn = classes === 'levels';
  const vMin = diverging ? -divLim : (useLog ? Math.pow(10, tMin) : tMin);
  const vMax = diverging ? divLim : (useLog ? Math.pow(10, tMax) : tMax);
  const levelBreaks = makeLevels({ min: vMin, max: vMax, log: useLog, interval, count });
  const nClasses = Math.max(1, levelBreaks.length - 1);
  const ramp = diverging && colormap === 'earthimager' ? 'bwr' : colormap;
  const colorOf = (v) => {
    if (levelsOn) return rampColor(ramp, (classify(v, levelBreaks) + 0.5) / nClasses, reverse);
    if (diverging) return ramp === 'bwr' && !reverse ? divergingColor(v, divLim) : rampColor(ramp, (v + divLim) / (2 * divLim), reverse);
    if (colormap === 'earthimager' && !reverse) return resistivityColor(useLog ? v : Math.pow(10, tf(v)), tMin, tMax);
    return rampColor(colormap, (tf(v) - tMin) / ((tMax - tMin) || 1), reverse);
  };
  const contourBreaks = contoursSame ? levelBreaks
    : makeLevels({ min: vMin, max: vMax, log: useLog, interval: contourInterval, count: contourCount });

  // Sensitivity → opacity mapping
  const sensVals = sensOpacity ? valid.map(c => c.sensitivity).filter(v => typeof v === 'number') : [];
  const sensMax = sensVals.length ? Math.max(...sensVals.slice(0, 50000)) : 1;
  const cellOpacity = (c) => {
    if (!sensOpacity || typeof c.sensitivity !== 'number' || sensMax <= 0) return 1;
    return Math.max(0.12, Math.min(1, c.sensitivity / sensMax));
  };

  const px = (x) => mL + ((x - x0) / ((x1 - x0) || 1)) * plotW;
  const py = (d) => mT + (d / maxDepth) * plotH;
  const rowH = (depth) => {
    const i = ds.indexOf(depth);
    const h = i + 1 < ds.length ? ds[i + 1] - depth : (i > 0 ? depth - ds[i - 1] : maxDepth / 10);
    return Math.max(1, (h / maxDepth) * plotH);
  };

  const legendStops = 24;
  // Surface line and DOI polyline (topography): one point per column of cells
  const surfaceByX = new Map();
  if (topo) valid.forEach(c => { if (typeof c.surface === 'number' && !surfaceByX.has(c.x)) surfaceByX.set(c.x, c.surface); });
  const surfacePts = topo ? [...surfaceByX.entries()].sort((a, b) => a[0] - b[0]).map(([x, z]) => [px(x), py(surfaceMax - z)]) : [];
  const nearestSurface = (x) => {
    let best = null;
    surfaceByX.forEach((z, cx) => { if (best === null || Math.abs(cx - x) < Math.abs(best[0] - x)) best = [cx, z]; });
    return best ? best[1] : surfaceMax;
  };
  // DOI: per column from the sensitivity (same algorithm as GET /api/inversion/{id}/doi),
  // else the importer's constant estimate. Follows the surface when there is topography.
  const doiProfile = computeDoiProfile(valid, doiSettings.threshold, model.doi);
  const doiVerts = doiLine(doiProfile);
  const doiPts = doiVerts.map(([x, d]) => [px(x), py(d + (topo ? surfaceMax - nearestSurface(x) : 0))]);
  const flatDoi = doiPts.length < 2 && typeof model.doi === 'number'; // 1D sounding / single column
  if (flatDoi) { const y = py(model.doi); doiPts.length = 0; doiPts.push([mL, y], [mL + plotW, y]); }
  const doiMean = doiVerts.length ? doiVerts.reduce((s, v) => s + v[1], 0) / doiVerts.length : model.doi;
  const doiLabel = typeof doiMean === 'number' ? `DOI ${doiMean.toFixed(1)} m` : 'DOI';
  const isBelowDoi = makeDoiMask(valid, { mask: doiSettings.mask, threshold: doiSettings.threshold, fallbackDoi: model.doi });
  const markerY = (x, e) => (topo ? py(surfaceMax - (typeof e?.z === 'number' ? e.z : nearestSurface(x))) : mT);
  const polyline = (pts) => pts.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%', display: 'block' }} preserveAspectRatio="xMidYMid meet">
      <rect data-testid="section-bg" x={0} y={0} width={W} height={H} fill={ap.frameBg} />
      <rect data-testid="section-plot-bg" x={mL} y={mT} width={plotW} height={plotH} fill={ap.plotBg} />
      {showTitle && <text data-testid="section-title" x={mL} y={mT - 8} {...ts} fontSize={Math.round(ap.fontSize * 1.3)} fontWeight={700}>{titleStr}</text>}
      <defs><clipPath id="section-contour-clip"><rect x={mL} y={mT} width={plotW} height={plotH} /></clipPath></defs>
      {/* Blocks */}
      {valid.map((c, i) => (
        // Mesh cells (EarthImager .out) carry their true bounds; point tables use the station grid
        <rect key={i}
          x={isPoint ? mL : c.x0 !== undefined ? px(c.x0) : px(c.x - dx / 2)} y={py(off(c) + (c.top !== undefined ? c.top : c.depth))}
          width={isPoint ? plotW : c.x1 !== undefined ? Math.max(1, px(c.x1) - px(c.x0)) + 0.5 : Math.max(1, (dx / ((x1 - x0) || 1)) * plotW) + 0.5}
          height={c.bottom !== undefined ? Math.max(1, py(c.bottom) - py(c.top)) + 0.5 : rowH(c.depth) + 0.5}
          opacity={cellOpacity(c)}
          fill={isBelowDoi(c) ? '#3b424c' : colorOf(c[property])} />
      ))}

      {/* Electrode / station markers along the surface */}
      {showElectrodes && (model.electrodes?.length && model.geometryKind !== 'point' ? model.electrodes : xs.map(x => ({ x }))).map((e, i) => {
        const y0 = markerY(e.x, e);
        return (
          <path key={`e${i}`} d={`M ${px(e.x) - 3.5} ${y0} L ${px(e.x) + 3.5} ${y0} L ${px(e.x)} ${y0 + 6} Z`}
            fill={ink} stroke={ap.plotBg} strokeWidth={0.8} opacity={0.9} />
        );
      })}
      {topo && surfacePts.length > 1 && (
        <polyline data-testid="section-surface" points={polyline(surfacePts)} fill="none" stroke={ink} strokeWidth={1.4} />
      )}

      {/* Fade below DOI + dashed DOI line (one polyline, per-column depth) */}
      {showDoi && doiPts.length > 1 && (
        <>
          <polygon data-testid="section-doi-fade"
            points={`${polyline([[mL, doiPts[0][1]], ...doiPts, [mL + plotW, doiPts[doiPts.length - 1][1]]])} ${mL + plotW},${mT + plotH} ${mL},${mT + plotH}`}
            fill={ap.plotBg} fillOpacity={0.55} />
          <polyline data-testid="section-doi" points={polyline(doiPts)} fill="none" stroke={ink} strokeWidth={1.2} strokeDasharray="7 4" opacity={0.85} />
          <text data-testid="section-doi-label" x={mL + plotW - 4} y={doiPts[doiPts.length - 1][1] - 4} {...tsSmall} textAnchor="end" stroke={ap.plotBg} strokeWidth={2.5} paintOrder="stroke">{doiLabel}</text>
        </>
      )}

      {contours && (
        <ContourLayer cells={cells} property={property} logOn={useLog} off={contourOff}
          px={px} py={py} breaks={contourBreaks} color={contourColor} labels={contourLabels} clip="url(#section-contour-clip)" ts={ts} />
      )}
      {overlay && overlay({ px, py, x0, x1, topo, surfaceMax, nearestSurface, mT, plotH })}
      {/* Axes */}
      {ap.showGrid && (
        <g data-testid="section-grid" stroke={ap.gridColor}>
          {[0, 0.25, 0.5, 0.75, 1].map(t => <line key={`gx${t}`} x1={mL + t * plotW} y1={mT} x2={mL + t * plotW} y2={mT + plotH} />)}
          {[0, 0.33, 0.66, 1].map(t => <line key={`gy${t}`} x1={mL} y1={mT + t * plotH} x2={mL + plotW} y2={mT + t * plotH} />)}
        </g>
      )}
      <rect x={mL} y={mT} width={plotW} height={plotH} fill="none" stroke={ap.axisColor} />
      {[0, 0.25, 0.5, 0.75, 1].map(t => (
        <g key={`x${t}`}>
          <text data-testid="section-axis-text" x={mL + t * plotW} y={mT + plotH + 14} {...tsSmall} textAnchor="middle">
            {(x0 + t * (x1 - x0)).toFixed(0)} m
          </text>
        </g>
      ))}
      {[0, 0.33, 0.66, 1].map(t => (
        <text key={`y${t}`} data-testid="section-axis-text" x={mL - 6} y={mT + t * plotH + 3} {...tsSmall} textAnchor="end">
          {(topo ? surfaceMax - t * maxDepth : t * maxDepth).toFixed(0)}
        </text>
      ))}
      <text data-testid="section-axis-text" x={12} y={mT + plotH / 2} {...tsSmall} transform={`rotate(-90 12 ${mT + plotH / 2})`} textAnchor="middle">{topo ? 'elevation (m)' : 'depth (m)'}</text>

      {/* Color scale legend */}
      {bottomLegend ? (
        <SectionLegendBottom testId="section-legend" x={mL} y={mT + plotH + 30} w={Math.min(plotW, 420)} textProps={tsSmall}
          colors={levelsOn
            ? Array.from({ length: nClasses }, (_, i) => rampColor(ramp, (i + 0.5) / nClasses, reverse))
            : Array.from({ length: legendStops }, (_, i) => {
              const t = tMin + (i / (legendStops - 1)) * (tMax - tMin);
              return diverging ? divergingColor(-divLim + (i / (legendStops - 1)) * 2 * divLim, divLim) : resistivityColor(useLog ? Math.pow(10, t) : t, tMin, tMax);
            })}
          ticks={levelsOn ? legendTicks(levelBreaks, useLog)
            : [diverging ? `-${divLim.toPrecision(3)}` : (useLog ? Math.pow(10, tMin) : tMin).toPrecision(3), diverging ? `+${divLim.toPrecision(3)}` : (useLog ? Math.pow(10, tMax) : tMax).toPrecision(3)]}
          unit={useLog ? 'Ωm (log)' : property} />
      ) : levelsOn ? (
        <g data-testid="section-legend">
          {Array.from({ length: nClasses }, (_, i) => (
            <rect key={`l${i}`} data-testid="section-legend-class" x={W - mR + 14} y={mT + plotH - ((i + 1) / nClasses) * plotH}
              width={12} height={plotH / nClasses + 0.5} fill={rampColor(ramp, (i + 0.5) / nClasses, reverse)} />
          ))}
          {legendTicks(levelBreaks, useLog).map((t, i) => (
            <text key={`t${i}`} data-testid="section-legend-tick" x={W - mR + 30} y={mT + plotH - (i / nClasses) * plotH + 3} {...tsSmall}>{t}</text>
          ))}
        </g>
      ) : bottomLegend ? null : Array.from({ length: legendStops }, (_, i) => {
        const t = tMin + (i / (legendStops - 1)) * (tMax - tMin);
        return (
          <rect key={`l${i}`} x={W - mR + 14} y={mT + plotH - ((i + 1) / legendStops) * plotH}
            width={12} height={plotH / legendStops + 0.5}
            fill={diverging ? divergingColor(-divLim + (i / (legendStops - 1)) * 2 * divLim, divLim) : resistivityColor(useLog ? Math.pow(10, t) : t, tMin, tMax)} />
        );
      })}
      {!bottomLegend && !levelsOn && <text x={W - mR + 30} y={mT + 8} {...tsSmall}>{diverging ? `+${divLim.toPrecision(3)}` : useLog ? Math.pow(10, tMax).toPrecision(3) : tMax.toPrecision(3)}</text>}
      {!bottomLegend && !levelsOn && <text x={W - mR + 30} y={mT + plotH} {...tsSmall}>{diverging ? `-${divLim.toPrecision(3)}` : useLog ? Math.pow(10, tMin).toPrecision(3) : tMin.toPrecision(3)}</text>}
      {!bottomLegend && <text x={W - mR + 20} y={H - 8} {...tsSmall} textAnchor="middle">{useLog ? 'Ωm (log)' : property}</text>}
    </svg>
  );
}

/**
 * SectionView — profile of grid values along a drawn polyline (Surfer-style)
 *
 * Draw a line on the map (Edit → Line), pick a grid, add the profile.
 * Multiple profiles overlay for comparing grids along the same path.
 *
 * Props:
 *   gridLayers - gridding result layers [{ id, name, geojson (metadata.grid) }]
 *   drawnLines - GeoJSON LineString features from Terra Draw
 *   inversionModels - [{ id, name, model, cells }] for the filled-section mode
 *   initialModelId  - preselect an inversion model (opens in section mode)
 *   renderOverlay({ entry, px, py, x0, x1, topo, surfaceMax, nearestSurface, mT, plotH }) - optional SVG drawn over the filled section (boreholes)
 *   onClose()
 */
export default function SectionView({ gridLayers = [], drawnLines = [], inversionModels = [], initialModelId = null, onClose, renderOverlay = null }) {
  const { C: themeC, theme } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [mode, setMode] = useState(initialModelId ? 'section' : 'profiles');
  const [modelId, setModelId] = useState(initialModelId || inversionModels[0]?.id || '');
  const [sectionProp, setSectionProp] = useState(
    () => (inversionModels.find(m => m.id === (initialModelId || inversionModels[0]?.id))?.model?.format === 'timelapse-diff' ? 'resistivity_pct_change' : 'resistivity'));
  const [sectionOpts, setSectionOpts] = useState(loadOpts);
  const [appearance, setAppearance] = useState(() => loadAppearance(theme));
  const setAppearancePatch = (patch) => setAppearance(a => { const n = { ...a, ...patch }; saveAppearance(n); return n; });
  const resetAppearance = () => { clearAppearance(); setAppearance(defaultAppearance(theme)); };
  const terrain = useTerrainSettings();
  const sectionSvgRef = useRef(null);

  const setOpt = (patch) => setSectionOpts(o => {
    const n = { ...o, ...patch };
    try { window.localStorage.setItem(OPTS_KEY, JSON.stringify(Object.fromEntries(PERSIST_KEYS.map(k => [k, n[k]])))); } catch { /* ignore */ }
    return n;
  });
  // Default the DEM surface ON when the project has a DEM and the user never chose either way
  const demTouched = useRef(false);
  const terrainRef = useRef(terrain);
  terrainRef.current = terrain;
  const demDatasetId = terrain.demDatasetId;
  useEffect(() => {
    if (!demDatasetId || demTouched.current) return;
    let touched = false;
    try { touched = window.localStorage.getItem('section.demTouched') === '1'; } catch { /* ignore */ }
    if (!touched) terrainRef.current.set({ useDemSurface: true });
    demTouched.current = true;
  }, [demDatasetId]);

  // Export the filled section as SVG or PNG
  const exportSection = (asPng) => {
    const svg = sectionSvgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    if (!asPng) {
      const url = URL.createObjectURL(svgBlob);
      const link = document.createElement('a');
      link.href = url; link.download = 'section.svg'; link.click();
      URL.revokeObjectURL(url);
      return;
    }
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const vb = svg.viewBox.baseVal;
      const canvas = document.createElement('canvas');
      canvas.width = vb.width * 2.5;
      canvas.height = vb.height * 2.5;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'section.png';
      link.click();
    };
    img.src = url;
  };
  const [gridId, setGridId] = useState('');
  const [lineIdx, setLineIdx] = useState(0);
  const [profiles, setProfiles] = useState([]); // { name, samples }
  const [error, setError] = useState(null);

  const usableGrids = gridLayers.filter(l => gridFromResult(l.geojson));
  const activeModel = inversionModels.find(m => m.id === modelId);
  // Project DEM surface (docs/DEM_TERRAIN.md): hangs a model without topography from the DEM
  const activeCells = useMemo(() => (activeModel ? sectionCells(activeModel.model) : null), [activeModel]);
  const demTerrain = useSectionTerrain(activeModel, activeCells);

  const addProfile = () => {
    setError(null);
    try {
      const layer = usableGrids.find(l => l.id === gridId);
      if (!layer) throw new Error('Select a grid layer');
      const line = drawnLines[lineIdx];
      if (!line) throw new Error('Draw a line on the map first (Edit → Line)');
      const g = gridFromResult(layer.geojson);
      const samples = profileAlongLine(g, line.geometry.coordinates, 150);
      const valid = samples.filter(s => s.value !== null);
      if (valid.length < 2) throw new Error('The line does not cross the grid extent');
      setProfiles(prev => [...prev.slice(-5), { name: `${layer.name} · line ${lineIdx + 1}`, samples }]);
    } catch (err) {
      setError(err.message);
    }
  };

  const exportCSV = () => {
    if (profiles.length === 0) return;
    const header = ['distance_m', ...profiles.map(p => `"${p.name.replace(/"/g, '""')}"`)].join(',');
    // Use the densest profile's distances as the row axis
    const base = profiles.reduce((a, b) => (a.samples.length >= b.samples.length ? a : b));
    const rows = base.samples.map((s, i) => [
      s.dist,
      ...profiles.map(p => p.samples[i]?.value ?? ''),
    ].join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'grid-profiles.csv'; link.click();
    URL.revokeObjectURL(url);
  };

  // Merge profiles for the chart (keyed by sample index → distance of first profile)
  const chartData = (() => {
    if (profiles.length === 0) return [];
    const base = profiles.reduce((a, b) => (a.samples.length >= b.samples.length ? a : b));
    return base.samples.map((s, i) => {
      const row = { dist: s.dist };
      profiles.forEach((p, pi) => { row[`p${pi}`] = p.samples[i]?.value ?? null; });
      return row;
    });
  })();

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };

  return (
    <div style={{ height: mode === 'section' ? 400 : 280, background: C.bg2, borderTop: `1px solid ${C.border}`, display: 'flex', flexShrink: 0 }}>
      {/* Controls */}
      <div style={{ width: 220, borderRight: `1px solid ${C.border}`, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>📐 Section / Profile</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text2, cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>

        {/* Mode toggle (when inversion models exist) */}
        {inversionModels.length > 0 && (
          <div style={{ display: 'flex', gap: 4 }}>
            {[{ id: 'profiles', label: 'Grid Profiles' }, { id: 'section', label: 'Inversion Section' }].map(m => (
              <button key={m.id} onClick={() => setMode(m.id)}
                style={{ flex: 1, fontSize: 10, padding: '4px 2px', borderRadius: 6, cursor: 'pointer', background: mode === m.id ? C.bg4 : 'transparent', color: mode === m.id ? C.accent : C.text2, border: `1px solid ${mode === m.id ? 'rgba(0,212,255,0.3)' : C.border}` }}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        {/* Inversion section controls */}
        {mode === 'section' && (
          <>
            <div>
              <label style={lbl}>Inversion Model</label>
              <select value={modelId} onChange={e => {
                const m = inversionModels.find(x => x.id === e.target.value);
                setModelId(e.target.value);
                if (m?.model?.format === 'timelapse-diff') setSectionProp('resistivity_pct_change');
                else if (!(m?.model?.columns || []).includes(sectionProp)) setSectionProp('resistivity');
              }} style={selStyle}>
                {inversionModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Property</label>
              <select value={sectionProp} onChange={e => setSectionProp(e.target.value)} style={selStyle}>
                {(activeModel?.model?.columns || []).filter(c => !['x', 'depth'].includes(c)).map(c =>
                  <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {activeModel?.model?.format === 'timelapse-diff' && (
              <TimelapseDateSlider C={C} model={activeModel.model} property={sectionProp} onChange={setSectionProp} />
            )}
            {activeModel && (
              <div style={{ fontSize: 9, color: C.text2, fontFamily: 'monospace', lineHeight: 1.7 }}>
                {activeModel.model.nCells.toLocaleString()} cells · x: {activeModel.model.xRange[0]}–{activeModel.model.xRange[1]} m · DOI {activeModel.model.doi} m
                {activeModel.model.rmsPercent != null && <> · RMS {activeModel.model.rmsPercent}%</>}
              </div>
            )}

            {/* EarthImager-style section controls */}
            <div>
              <label style={lbl}>Vertical Exaggeration — {sectionOpts.ve.toFixed(1)}×</label>
              <input type="range" min={0.5} max={6} step={0.25} value={sectionOpts.ve}
                onChange={e => setOpt({ ve: parseFloat(e.target.value) })}
                style={{ width: '100%', accentColor: C.accent }} />
            </div>
            <div style={{ ...lbl, marginTop: 4, fontWeight: 700 }}>Color scale</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select data-testid="section-colormap" value={sectionOpts.colormap} onChange={e => setOpt({ colormap: e.target.value })}
                style={{ ...selStyle, flex: 1, boxSizing: 'border-box' }} aria-label="Colormap">
                {SECTION_COLORMAPS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
                <input type="checkbox" checked={sectionOpts.reverse} onChange={e => setOpt({ reverse: e.target.checked })} style={{ accentColor: C.accent }} />
                Reverse
              </label>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Classes</label>
                <select data-testid="section-classes" value={sectionOpts.classes} onChange={e => setOpt({ classes: e.target.value })}
                  style={{ ...selStyle, boxSizing: 'border-box' }}>
                  <option value="levels">levels</option>
                  <option value="continuous">continuous</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Interval{sectionOpts.logScale ? ' (decades)' : ''}</label>
                <input type="number" value={sectionOpts.interval} placeholder="auto" disabled={sectionOpts.classes !== 'levels'}
                  onChange={e => setOpt({ interval: e.target.value })} style={{ ...selStyle, boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Count</label>
                <input type="number" min={1} max={39} value={sectionOpts.count} disabled={sectionOpts.classes !== 'levels'}
                  onChange={e => setOpt({ count: e.target.value })} style={{ ...selStyle, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Scale Min</label>
                <input type="number" value={sectionOpts.cmin} placeholder="auto"
                  onChange={e => setOpt({ cmin: e.target.value })}
                  style={{ ...selStyle, boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Scale Max</label>
                <input type="number" value={sectionOpts.cmax} placeholder="auto"
                  onChange={e => setOpt({ cmax: e.target.value })}
                  style={{ ...selStyle, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                ['logScale', 'Logarithmic color scale'],
                ['showDoi', 'Show DOI line + fade (threshold set in the Inversion panel)'],
                ['sensOpacity', 'Sensitivity as opacity'],
                ['showElectrodes', 'Electrode markers'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sectionOpts[key]}
                    onChange={e => setOpt({ [key]: e.target.checked })}
                    style={{ accentColor: C.accent }} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ ...lbl, marginTop: 4, fontWeight: 700 }}>Contours</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
              <input type="checkbox" data-testid="section-contours" checked={sectionOpts.contours}
                onChange={e => setOpt({ contours: e.target.checked })} style={{ accentColor: C.accent }} />
              Show contour lines
            </label>
            {sectionOpts.contours && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sectionOpts.contoursSame} onChange={e => setOpt({ contoursSame: e.target.checked })} style={{ accentColor: C.accent }} />
                  Same levels as colour scale
                </label>
                {!sectionOpts.contoursSame && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="number" placeholder="interval" value={sectionOpts.contourInterval} onChange={e => setOpt({ contourInterval: e.target.value })} style={{ ...selStyle, boxSizing: 'border-box' }} />
                    <input type="number" placeholder="count" value={sectionOpts.contourCount} onChange={e => setOpt({ contourCount: e.target.value })} style={{ ...selStyle, boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select value={sectionOpts.contourColor} onChange={e => setOpt({ contourColor: e.target.value })} style={{ ...selStyle, flex: 1, boxSizing: 'border-box' }} aria-label="Line colour">
                    <option value="black">black lines</option><option value="white">white lines</option><option value="grey">grey lines</option>
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sectionOpts.contourLabels} onChange={e => setOpt({ contourLabels: e.target.checked })} style={{ accentColor: C.accent }} />
                    Labels
                  </label>
                </div>
              </>
            )}
            <label title={terrain.demDatasetId ? '' : 'Mark a raster as the project DEM first'}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: terrain.demDatasetId ? C.text2 : C.text2, cursor: terrain.demDatasetId ? 'pointer' : 'default' }}>
              <input type="checkbox" data-testid="section-dem-surface" disabled={!terrain.demDatasetId}
                checked={!!terrain.demDatasetId && terrain.useDemSurface}
                onChange={e => { demTouched.current = true; try { window.localStorage.setItem('section.demTouched', '1'); } catch { /* ignore */ } terrain.set({ useDemSurface: e.target.checked }); }} style={{ accentColor: C.accent }} />
              DEM surface (project DEM)
            </label>
            <SectionAppearanceControls appearance={appearance} onChange={setAppearancePatch} onReset={resetAppearance} C={C} selStyle={selStyle} lbl={lbl} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => exportSection(true)}
                style={{ flex: 1, fontSize: 10, padding: '5px', borderRadius: 6, background: C.bg4, color: C.text1, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                🖼 PNG
              </button>
              <button onClick={() => exportSection(false)}
                style={{ flex: 1, fontSize: 10, padding: '5px', borderRadius: 6, background: C.bg4, color: C.text1, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                📐 SVG
              </button>
            </div>
          </>
        )}

        {mode === 'profiles' && (<>
        <div>
          <label style={lbl}>Grid Layer</label>
          <select value={gridId} onChange={e => setGridId(e.target.value)} style={selStyle}>
            <option value="">Select grid...</option>
            {usableGrids.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Profile Line (drawn on map)</label>
          <select value={lineIdx} onChange={e => setLineIdx(parseInt(e.target.value))} style={selStyle}>
            {drawnLines.length === 0 && <option value={0}>No lines drawn yet</option>}
            {drawnLines.map((_, i) => <option key={i} value={i}>Line {i + 1} ({drawnLines[i].geometry.coordinates.length} vertices)</option>)}
          </select>
        </div>

        <button onClick={addProfile} disabled={!gridId || drawnLines.length === 0}
          style={{ padding: '7px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#0e7490,#1d4ed8)', color: '#fff', fontWeight: 600, fontSize: 11, cursor: 'pointer', opacity: (!gridId || drawnLines.length === 0) ? 0.5 : 1 }}>
          + Add Profile
        </button>

        {profiles.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {profiles.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10 }}>
                  <span style={{ width: 10, height: 3, background: PROFILE_COLORS[i % PROFILE_COLORS.length], borderRadius: 2 }} />
                  <span style={{ color: C.text2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  <button onClick={() => setProfiles(prev => prev.filter((_, pi) => pi !== i))}
                    style={{ background: 'none', border: 'none', color: C.text2, cursor: 'pointer', fontSize: 10 }}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={exportCSV}
              style={{ padding: '5px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, fontSize: 10, cursor: 'pointer' }}>
              💾 Export CSV
            </button>
          </>
        )}

        {error && <div style={{ fontSize: 10, color: C.red, padding: '4px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {error}</div>}
        {drawnLines.length === 0 && (
          <div style={{ fontSize: 10, color: C.warnText, lineHeight: 1.5 }}>
            ⚠ Draw a profile line first: Edit tab → Line, click vertices on the map.
          </div>
        )}
        </>)}
      </div>

      {/* Chart / filled section */}
      <div style={{ flex: 1, padding: '10px 14px', minWidth: 0 }}>
        {mode === 'section' ? (
          activeModel ? (
            <div style={{ height: '100%', overflow: 'auto' }}>
              <FilledSection model={demTerrain.model} cells={demTerrain.cells}
                property={sectionProp} svgRef={sectionSvgRef} options={sectionOpts} appearance={appearance} title={activeModel.name}
                overlay={(renderOverlay || demTerrain.surface) ? (g) => (
                  <>
                    {demTerrain.surface && <SectionTerrainOverlay {...g} demSurface={demTerrain.surface} applied={demTerrain.applied} />}
                    {renderOverlay && renderOverlay({ ...g, entry: activeModel })}
                  </>
                ) : null} />
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text2, fontSize: 12 }}>
              Import an inversion model first (⚡ Inversion panel).
            </div>
          )
        ) : chartData.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text2, fontSize: 12, textAlign: 'center' }}>
            Distance vs grid value along a drawn line.<br />
            Add profiles from different grids to compare them on the same section.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="dist" type="number" domain={[0, 'dataMax']}
                tick={{ fontSize: 9, fill: C.text2 }} stroke={C.border}
                label={{ value: 'distance along line (m)', position: 'insideBottom', offset: -8, fontSize: 10, fill: C.text2 }}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
              <YAxis type="number" allowDataOverflow tick={{ fontSize: 9, fill: C.text2 }} stroke={C.border}
                domain={paddedDomain(profiles.flatMap(p => p.samples.map(s => s.value)))}
                tickFormatter={v => Number(v).toPrecision(3)} />
              <ChartTooltip contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }}
                labelFormatter={v => `${v} m`}
                formatter={(v) => typeof v === 'number' ? v.toPrecision(4) : v} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {profiles.map((p, i) => (
                <Line key={i} name={p.name} dataKey={`p${i}`}
                  stroke={PROFILE_COLORS[i % PROFILE_COLORS.length]} strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
